import type { CoveredWindow, OpenWindow } from '@familyhub/shared';
import { formatTimeRange } from './datetime';

/**
 * A day's childcare coverage, as one strip — and **only when there is a gap**.
 *
 * This is the module's answer to its own worst habit. Coverage is an interval
 * property of a *day*: the engine computes it that way and even merges each
 * day's open windows "so overlapping gaps from two activities read as one".
 * The week then threw that away and reported the result per *activity*, so a
 * single uncovered Tuesday morning was announced separately on every activity
 * overlapping it — two activities, two identical red sentences, and it got
 * worse the busier the day was, which is exactly when the screen matters most.
 *
 * Stated once, positioned in time, it needs no sentence per row: the bar shows
 * *where* in the day the hole is, which no amount of repetition ever did.
 *
 * **A calm day renders nothing.** Not a reassurance strip, not an "alles
 * geregeld ✓" — the same rule the rest of the module follows. A covered day
 * already says who has the child in the care chips above; this exists purely to
 * locate a problem, so with no problem there is nothing to locate.
 */
export function CoverageBar({
  gaps,
  covered,
  labelFor,
}: {
  gaps: OpenWindow[];
  covered: CoveredWindow[];
  labelFor: (key: string) => { name: string; color: string };
}) {
  if (gaps.length === 0) return null;

  // The extent the bar spans: everything it has to draw, padded to a round-ish
  // edge so the first block does not start flush against the rail.
  const marks = [...gaps, ...covered].flatMap((w) => [Date.parse(w.starts_at), Date.parse(w.ends_at)]);
  const from = Math.min(...marks);
  const to = Math.max(...marks);
  const span = to - from;
  if (!(span > 0)) return null;

  const pct = (ms: number) => ((ms - from) / span) * 100;
  const block = (w: { starts_at: string; ends_at: string }) => {
    const left = pct(Date.parse(w.starts_at));
    // A floor on the width: a fifteen-minute hole in a twelve-hour day is a
    // sliver too thin to see, and an invisible warning is not a warning.
    return { left: `${left}%`, width: `${Math.max(2.5, pct(Date.parse(w.ends_at)) - left)}%` };
  };

  const windows = gaps.map((g) => formatTimeRange(g.starts_at, g.ends_at)).join(', ');

  return (
    <div className="agenda-cover">
      <div className="agenda-cover-rail" aria-hidden>
        {covered.map((w, i) => (
          <span
            key={`c${i}`}
            className="agenda-cover-block"
            style={{ ...block(w), background: labelFor(w.carerKeys[0] ?? '').color }}
          />
        ))}
        {gaps.map((w, i) => (
          <span key={`g${i}`} className="agenda-cover-block agenda-cover-gap" style={block(w)} />
        ))}
      </div>
      <p className="agenda-cover-line">
        ⚠️ waar is de kleine tussen {windows}?
      </p>
    </div>
  );
}
